import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as screenfull from 'screenfull';

import { Platform } from '@angular/cdk/platform';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { SnCamera } from '@lib/api/entities';
import { SnCurrentUser } from '@lib/auth';
import { SnAspectRatio } from '@lib/common/enums';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { ISnPlayer, SnPlayerState } from '@lib/player/common';
import { ITimelineDefinition, TimelineComponent } from '@lib/timeline';

import { SnSldpPlayerReference } from '../../references';
import { StreamAnalysisService } from '../../services/stream-analysis-service';

@Component({
  selector: 'fs-bosch-player',
  templateUrl: './bosch-player.component.html',
  styleUrls: ['./bosch-player.component.scss'],
  providers: [
    {
      provide: 'SN_PLAYER',
      useExisting: forwardRef(() => FsBoschPlayerComponent),
    },
  ],
  host: {
    class: 'fs-bosch-player',
  },
})
export class FsBoschPlayerComponent implements OnInit, OnDestroy, AfterViewInit, ISnPlayer {
  get video(): any {
    const videoElement = this.elementRef.nativeElement;
    return videoElement.querySelector('video');
  }

  get parent(): any {
    return this.video.parentElement.parentElement;
  }

  get live$(): Observable<boolean> {
    return this._live$.asObservable();
  }

  get state$(): Observable<SnPlayerState> {
    return this._state$.asObservable();
  }

  get play$(): Observable<SnPlayerState> {
    return this._state$.pipe(filter((state) => state === SnPlayerState.Playing));
  }

  get pause$(): Observable<SnPlayerState> {
    return this._state$.pipe(filter((state) => state === SnPlayerState.Pause));
  }

  get end$(): Observable<SnPlayerState> {
    return this._state$.pipe(filter((state) => state === SnPlayerState.Ended));
  }

  get currentTime$(): Observable<number> {
    return this._currentTime$.asObservable();
  }

  get loaded$(): Observable<boolean> {
    return this._loaded$.asObservable();
  }

  get bufferTime$(): Observable<number> {
    return this._bufferedTime$.asObservable();
  }

  get totalTime$(): Observable<number> {
    return this._totalTime$.asObservable();
  }

  get volume$(): Observable<number> {
    return this._volume$.asObservable();
  }

  get connected$(): Observable<boolean> {
    return this._connected$.asObservable();
  }

  get played$(): Observable<any> {
    return this._played$.asObservable();
  }

  get fullscreen$(): Observable<boolean> {
    return this._fullscreen$.asObservable();
  }

  get ptzOn$(): Observable<boolean> {
    return this._ptzOn$.asObservable();
  }

  get timelineLoaded$(): Observable<Date> {
    return this._timelineLoaded$.asObservable();
  }

  get ended$(): Observable<void> {
    return this._ended$.asObservable();
  }

  get currentTime(): number {
    return this._currentTime$.value;
  }

  get isLoading(): boolean {
    return false;
  }

  get isPlaying(): boolean {
    return true;
  }

  get live(): boolean {
    return this._live;
  }

  set live(value: boolean) {
    if (value) {
      this._assignStreams();
    }
    this._live = value;
  }

  get liveStatus(): boolean {
    return this._live$.value;
  }

  @Input()
  public src: SnCamera;

  @Input()
  public ptzConfig: any;

  @Input()
  public autoplay: boolean = true;

  @Input()
  public showCalendar: boolean = false;

  @Input()
  public width: string | number;

  @Input()
  public height: string | number;

  @Input()
  public aspectRatio: number | SnAspectRatio;

  @Input()
  public caption: string;

  @Input()
  public alwaysOn: string;

  @ViewChild('playerElement') public playerElement: ElementRef;

  @ViewChild('playerContent', { read: ElementRef, static: true })
  public readonly _playerContentElementRef: ElementRef<HTMLElement>;

  @ViewChild('recordingTimeline') public timelineRef: TimelineComponent;

  @Output() public readonly fullscreenChange = new EventEmitter<boolean>();

  public mode: string = 'Live';
  public timeline: ITimelineDefinition;
  public date = new Date();
  public time = new Date();
  public fullscreen: boolean;
  public readonly _screenfull = screenfull;
  public playerState: SnPlayerState;
  public isBosch = true;
  public sessionId;
  public ptzOn: boolean;
  public hasRecordings = this._hasRecordings.bind(this);

  private _playbackPlayer;
  private _sldpPlayerRef: SnSldpPlayerReference;
  private _videoElement: HTMLVideoElement;
  private _live: boolean;
  private readonly _videoEventListeners = new Map<string, EventListenerOrEventListenerObject>();

  private readonly _live$ = new BehaviorSubject<boolean>(false);
  private readonly _currentTime$ = new BehaviorSubject<number>(0);
  private readonly _loaded$ = new Subject<boolean>();
  private readonly _bufferedTime$ = new BehaviorSubject<number>(0);
  private readonly _totalTime$ = new BehaviorSubject<number>(0);
  private readonly _volume$ = new BehaviorSubject<number>(0);
  private readonly _state$ = new BehaviorSubject<SnPlayerState>(SnPlayerState.Pending);
  private readonly _destroy$ = new Subject<void>();
  private readonly _connected$ = new BehaviorSubject<boolean>(false);
  private readonly _played$ = new BehaviorSubject<any>(null);
  private readonly _fullscreen$ = new BehaviorSubject<boolean>(false);
  private readonly _ptzOn$ = new BehaviorSubject<boolean>(false);
  private readonly _timelineLoaded$ = new BehaviorSubject<Date>(null);
  private readonly _ended$ = new Subject<void>();

  constructor(
    public elementRef: ElementRef,
    private readonly _http: HttpClient,
    private readonly _platform: Platform,
    private readonly _cd: ChangeDetectorRef,
    private readonly _datadogService: FsDataDogRumService,
    private readonly _currentUser: SnCurrentUser,
    private readonly _streamAnalysisService: StreamAnalysisService,
  ) {}

  @HostListener('document:keydown.space')
  public togglePause(): void {
    if (!this.fullscreen) {
      return;
    }
    if (this.playerState === SnPlayerState.Playing) {
      this.pause();
    } else if (this.playerState === SnPlayerState.Pause) {
      this.play();
    }
  }

  public setPTZ(on: boolean): void {
    this.ptzOn = on;
    this._ptzOn$.next(on);
  }

  public play(): void {
    this._videoElement.play();
  }

  public pause(): void {
    this._videoElement.pause();
  }

  public ngOnInit(): void {
    this.date.setHours(0, 0, 0, 0);
    this._state$.pipe(takeUntil(this._destroy$)).subscribe((state) => {
      this.playerState = state;
    });

    this.connected$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._live$.next(true);
      this._loadTimeline();
    });

    this.timelineLoaded$.pipe(takeUntil(this._destroy$)).subscribe((date) => {
      if (date && this._playbackPlayer) {
        const time = date.getTime();
        this._playbackPlayer.seek(time);
        this.time = date;
      }
    });
  }

  public ngAfterViewInit(): void {
    this._assignStreams();
  }

  public exitedFullscreen(fullscreen?: boolean): void {
    this._ptzOn$.next(false);
    this.fullscreen = false;
    this._fullscreen$.next(this.fullscreen);
    this.fullscreenChange.next(this.fullscreen);
    this._cd.detectChanges();
  }

  public setMode(mode: string): void {
    this.mode = mode;
    switch (mode) {
      case 'Live':
        this._initLive();
        break;
      case 'Recorded':
        this._initPlayback();
        break;
    }
  }

  public changeFullscreen(fullscreen?: boolean): void {
    this.fullscreen = !this.fullscreen;
    this._fullscreen$.next(this.fullscreen);
    this.fullscreenChange.next(this.fullscreen);
    const element = this.elementRef.nativeElement;
    if (this._screenfull.isEnabled) {
      if (this._screenfull.isFullscreen) {
        this._screenfull.exit();
      } else {
        this._screenfull.request(element);
      }
    } else {
      if (this.fullscreen) {
        element.classList.add('fullscreen');
      } else {
        element.classList.remove('fullscreen');
      }
    }
  }

  public moveCursor(date: Date): void {
    if (this._playbackPlayer) {
      this._playbackPlayer.seek(date.getTime());
    } else {
      if (this._sldpPlayerRef) {
        this._sldpPlayerRef.destroy();
      }
      this._initPlayback(date);
    }
    this._live$.next(false);
    this._live = false;
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _initLive(): void {
    const element = `<bosch-player
    url-websocket="${this.src.liveVideoUrls.main.cloud.wssRtsp}"
    url-rtsp="rtsp://127.0.0.1/rtsp_tunnel?h26x=4&line=1&inst=1&rec=0&live=1&vcd=1&enableaudio=0&skipbframes=1&skipsei=0&rnd=[rnd]"
    username="${this.src?.username}"
    password="${this.src?.password}"
    play
    retry></bosch-player>`;
    this.elementRef.nativeElement.querySelector('.bosch-player').innerHTML = element;

    const livePlayer = this.elementRef.nativeElement.querySelector('bosch-player');
    this._live$.next(true);
    this._state$.next(SnPlayerState.Playing);

    livePlayer.addEventListener('onready', () => {
      if (!this.sessionId) {
        this.sessionId = livePlayer.sessionId;
      }
      this._videoElement = livePlayer.querySelector('video');
      this._destroyVideoEventListeners();
      this._initVideoEvents();
      this._state$.next(SnPlayerState.Playing);
    });

    livePlayer.addEventListener('onupdatecurrenttime', (event) => {
      this.time = event.detail?.timestamp;
    });
  }

  private _initPlayback(date?: Date): void {
    this.sessionId = null;
    this.time = new Date();
    this._live = true;
    if (!this.src.playbackUrls) {
      this._initLive();
      return;
    }
    const element = `<bosch-playback
      url-websocket="${this.src.playbackUrls.main.local.wssRtsp}"
      url-rtsp="rtsp://127.0.0.1/rtsp_tunnel?h26x=4&line=1&inst=1&rec=1&live=1&vcd=1&enableaudio=0&skipbframes=1&skipsei=0&rnd=[rnd]"
      command-session="${this.src.playbackUrls.main.local.boschSession}"
      command-tracks="${this.src.playbackUrls.main.local.boschTracksRetrieve}"
      command-seek="${this.src.playbackUrls.main.local.boschPlaybackSeek}"
      command-replay="${this.src.playbackUrls.main.local.boschPlaybackReplayStart}"
      command-messages="${this.src.playbackUrls.main.local.boschMessage}"
      command-timezone="${this.src.playbackUrls.main.local.boschTimezoneRetrieve}"
      username="${this.src?.username}"
      password="${this.src?.password}"
      play
      retry
      ></bosch-playback>`;
    this.elementRef.nativeElement.querySelector('.bosch-player').innerHTML = element;
    this._playbackPlayer = this.elementRef.nativeElement.querySelector('bosch-playback');
    this._live$.next(true);

    this._playbackPlayer.addEventListener('onready', () => {
      if (!this.sessionId) {
        this.sessionId = this._playbackPlayer.sessionId;
      }
      this._videoElement = this._playbackPlayer.querySelector('video');
      this._destroyVideoEventListeners();
      this._initVideoEvents();
      this._state$.next(SnPlayerState.Playing);
      this._loadTimeline(this.sessionId, date);
    });

    this._playbackPlayer.addEventListener('onupdatecurrenttime', (event) => {
      this.time = event.detail?.timestamp;
    });
  }

  private _loadTimeline(id?: number, date?: Date): void {
    if (id) {
      const url = this.src.playbackUrls.main.cloud.management.replace('[sessionid]', id.toString());
      this._http
        .get(url)
        .pipe(takeUntil(this._destroy$))
        .subscribe((timeline) => {
          this.timeline = timeline[0];
          if (date) {
            this._timelineLoaded$.next(date);
          }
        });
    } else {
      const duration = this.src.loopDuration;
      const now = Date.now();
      const dateFrom = new Date(now - duration * 60000);
      this.timeline = {
        duration,
        timeline: [
          {
            start: Math.ceil(dateFrom.getTime() / 1000),
            end: Math.floor(now / 1000),
            duration,
          },
        ],
      };
    }
    if (this.timelineRef) {
      this.timelineRef.timeline.redraw();
      this._cd.detectChanges();
    }
  }

  private _initVideoEvents(): void {
    this._videoEventListeners.set('play', () => this._state$.next(SnPlayerState.Playing));
    this._videoEventListeners.set('pause', () => this._state$.next(SnPlayerState.Pause));
    this._videoEventListeners.set('seeked', () => this._loaded$.next(false));
    this._videoEventListeners.set('canplaythrough', () => {
      this._loaded$.next(true);
    });
    this._videoEventListeners.set('ended', () => this._state$.next(SnPlayerState.Ended));
    this._videoEventListeners.set('volumechange', () => {
      if (!this._videoElement.muted) {
        this._volume$.next(this._videoElement.volume);
      }
    });
    this._videoEventListeners.set('timeupdate', () => {
      this._currentTime$.next(this._videoElement.currentTime);
      this._played$.next(this._videoElement.played);
    });
    this._videoEventListeners.set('durationchange', () => {
      this._totalTime$.next(this._videoElement.duration);
    });

    this._videoEventListeners.forEach((listener, event) => {
      this._videoElement?.addEventListener(event, listener);
    });
  }

  private _destroyVideoEventListeners(): void {
    this._videoEventListeners.forEach((listener, event) => {
      this._videoElement.removeEventListener(event, listener);
    });
  }

  private _loaded(): void {
    this._connected$.next(true);
    this._videoElement = this.elementRef.nativeElement.querySelector('video');
    this._cd.detectChanges();
  }

  private _error(): void {
    this._connected$.next(false);
    this._cd.detectChanges();
  }

  private _hasRecordings(date: Date): boolean {
    const t = this.timeline;
    const testDate = new Date(date.getTime());
    const dateStart = testDate.getTime();
    testDate.setDate(testDate.getDate() + 1);
    const dateEnd = testDate.getTime();
    const timeline = t.timeline.filter((stream) => {
      return dateStart < stream.end * 1000 && stream.start * 1000 < dateEnd;
    });
    return timeline.length > 0;
  }

  private _assignStreams(): void {
    if (this.ptzConfig) {
      this._initPlayback();
    } else {
      if (this._sldpPlayerRef) {
        this._sldpPlayerRef.destroy();
      }
      const source = this.src.liveVideoUrls.main;
      this._sldpPlayerRef = source.local.sldp
        ? this._initSldpPlayerRef(
            source.local.sldp,
            () => this._loaded(),
            () => this._error(),
          )
        : (this._sldpPlayerRef = this._initSldpPlayerRef(
            source.cloud.sldp,
            () => this._loaded(),
            () => this._error(),
          ));
      this._live = true;
      this._sldpPlayerRef.currentTime$
        .pipe(takeUntil(this._sldpPlayerRef.destroyed$), takeUntil(this._destroy$))
        .subscribe(() => {
          if (this._live) {
            this.time = new Date();
            return;
          }
        });
    }
  }

  private _initSldpPlayerRef(
    src: string,
    callback: Function,
    error: Function,
  ): SnSldpPlayerReference {
    return new SnSldpPlayerReference(
      this.playerElement.nativeElement,
      this._playerContentElementRef.nativeElement,
      src,
      this.autoplay,
      this._platform,
      this._state$,
      this._currentTime$,
      this._bufferedTime$,
      this._totalTime$,
      this._volume$,
      this._played$,
      this._loaded$,
      this._ended$,
      callback,
      error,
      this._datadogService,
      this.src.id,
      this._currentUser.settings?.userGlobalInterfaceConfig?.sldpSettings,
      this._streamAnalysisService,
      this.src,
    );
  }
}
